import {
  Component,
  inject,
  OnInit,
  output,
  ChangeDetectionStrategy,
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { CommonModule } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { ModelStudentprofile } from "src/app/model/model.studentprofile";
import { ProfileLookingFor } from "src/app/api/models";
import {
  ProfileLookingForOption,
  ProfileLookingForUtils,
} from "src/app/shared/utils/student-profile.utils";

export interface StudentAnnouncementData {
  type: ProfileLookingFor;
  motivation: string;
  startDate: string;
  endDate?: string;
}

@Component({
  selector: "app-modal-student-profile-announcement",
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule],
  templateUrl: "./modal-student-profile-announcement.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalStudentProfileAnnouncementComponent implements OnInit {
  private fb = inject(FormBuilder);
  public activeModal = inject(NgbActiveModal);

  // Inputs for configuration
  data!: ModelStudentprofile; // Receives data from modal service
  announcementSubmit = output<StudentAnnouncementData>();

  announcementForm!: FormGroup;
  isSubmitting = false;
  minDate: string;
  motivationMinLength = 10;
  ProfileLookingFor = ProfileLookingFor; // Expose enum for template use

  readonly lookingForOptions: ProfileLookingForOption[] =
    ProfileLookingForUtils.getOptions();

  constructor() {
    // Set minimum date to today
    const today = new Date();
    this.minDate = today.toISOString().split("T")[0];
  }

  ngOnInit(): void {
    this.initializeForm();
  }

  private initializeForm(): void {
    this.announcementForm = this.fb.group({
      type: [this.data?.lookingFor || "", [Validators.required]],
      motivation: [
        "",
        [Validators.required, Validators.minLength(this.motivationMinLength)],
      ],
      startDate: ["", [Validators.required]],
      endDate: [""],
    });

    this.setupFormValidation();
  }

  private setupFormValidation(): void {
    // Watch for type changes to handle end date validation
    this.announcementForm.get("type")?.valueChanges.subscribe((type) => {
      this.handleEndDateValidation(type);
    });

    // Watch for start date changes to update end date minimum
    this.announcementForm
      .get("startDate")
      ?.valueChanges.subscribe((startDate) => {
        this.handleStartDateChange(startDate);
      });
  }

  private handleEndDateValidation(type: ProfileLookingFor): void {
    const endDateControl = this.announcementForm.get("endDate");

    if (type === ProfileLookingFor.Stage) {
      endDateControl?.setValidators([Validators.required]);
    } else {
      endDateControl?.clearValidators();
      endDateControl?.setValue("");
    }
    endDateControl?.updateValueAndValidity();
  }

  private handleStartDateChange(startDate: string): void {
    if (!startDate) return;

    const endDateControl = this.announcementForm.get("endDate");
    if (endDateControl?.value && endDateControl.value < startDate) {
      endDateControl.setValue("");
    }
  }

  get selectedType(): ProfileLookingFor {
    return this.announcementForm.get("type")?.value || "";
  }

  get startDateValue(): string {
    return this.announcementForm.get("startDate")?.value || this.minDate;
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.announcementForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  onSubmit(): void {
    if (!this.announcementForm.valid) {
      this.announcementForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    this.announcementSubmit.emit(this.announcementForm.value);
  }

  closeModal(): void {
    this.activeModal.close();
  }

  private isFormValid(): boolean {
    return this.announcementForm.valid && !this.isSubmitting;
  }

  // Convenience method for parent to get form data without closing
  getFormData(): StudentAnnouncementData | null {
    return this.isFormValid() ? this.announcementForm.value : null;
  }
}
